"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import SiteHeader from "@/app/components/SiteHeader";
import { MagnetCapture } from "@/components/shared/MagnetCapture";

type Gender = "male" | "female";

const REFERENCE_TABLE: Record<Gender, Array<{ age: string; poor: number; below: number; mid: number; good: number; excellent: number }>> = {
  male: [
    { age: "20–29", poor: 38, below: 42, mid: 49, good: 56, excellent: 56 },
    { age: "30–39", poor: 35, below: 39, mid: 45, good: 52, excellent: 52 },
    { age: "40–49", poor: 31, below: 36, mid: 42, good: 48, excellent: 48 },
    { age: "50–59", poor: 26, below: 31, mid: 38, good: 45, excellent: 45 },
    { age: "60+", poor: 22, below: 27, mid: 33, good: 40, excellent: 40 },
  ],
  female: [
    { age: "20–29", poor: 31, below: 35, mid: 40, good: 47, excellent: 47 },
    { age: "30–39", poor: 28, below: 33, mid: 38, good: 44, excellent: 44 },
    { age: "40–49", poor: 25, below: 29, mid: 34, good: 41, excellent: 41 },
    { age: "50–59", poor: 22, below: 27, mid: 32, good: 38, excellent: 38 },
    { age: "60+", poor: 18, below: 23, mid: 28, good: 34, excellent: 34 },
  ],
};

function ageGroup(age: number): number {
  if (age < 30) return 0;
  if (age < 40) return 1;
  if (age < 50) return 2;
  if (age < 60) return 3;
  return 4;
}

type Vurdering = { level: string; color: string; label: string };

function classify(vo2: number, gender: Gender, age: number): Vurdering {
  const row = REFERENCE_TABLE[gender][ageGroup(age)];
  if (vo2 < row.poor) return { level: "poor", color: "text-red-600", label: "Dårlig" };
  if (vo2 < row.below) return { level: "below", color: "text-amber-600", label: "Under middels" };
  if (vo2 < row.mid) return { level: "mid", color: "text-stone-700", label: "Middels" };
  if (vo2 < row.good) return { level: "good", color: "text-emerald-600", label: "God" };
  return { level: "excellent", color: "text-emerald-700", label: "Utmerket" };
}

const TIPS_BY_LEVEL: Record<string, string[]> = {
  poor: [
    "Start med 3 økter i uken på 30 min — gå raskt eller jogg sakte. Bygg basen først.",
    "Hold deg i sone 2 (60–70 % av maks puls) — du skal kunne snakke i hele setninger.",
    "Etter 4 uker: legg til ÉN intervalløkt — 4×3 min på høyere intensitet med 2 min pause.",
  ],
  below: [
    "Gjør én intervalløkt i uken — 4×4 min på 85–90 % maks puls med 3 min pause mellom.",
    "Behold 2 lengre, rolige økter (45–60 min) for å bygge utholdenhet.",
    "Følg fremgangen i 8–12 uker. Studier viser 5–10 % forbedring er realistisk på den tiden.",
  ],
  mid: [
    "Norsk forskning (NTNU) viser at 4×4-intervaller gir best forbedring av VO2 Max — kjør 1–2 ganger/uke.",
    "Legg til en lang økt (90 min) annenhver uke for økt aerob kapasitet.",
    "Vær tålmodig: progresjon over 12+ uker gir varig forbedring, ikke 4 uker av intensitet.",
  ],
  good: [
    "På dette nivået må du periodisere — 4 uker med fokus på VO2 Max, deretter 2 uker rolig.",
    "Vurder backe-til-backe-intervaller eller bakkeløp for ny stimulus.",
    "Restitusjon blir kritisk — søvn, ernæring og rolige dager avgjør om du fortsetter å forbedres.",
  ],
  excellent: [
    "Du er i toppen 5 % nasjonalt — videre forbedring krever målrettet idretts-coaching.",
    "Sjekk om du er i området der genetikk og treningsalder bremser progresjonen.",
    "Vurder spesifikke testprotokoller (laktatterskel, FTP) for finjustering av treningssoner.",
  ],
};

export default function VO2MaxCalculator() {
  const [gender, setGender] = useState<Gender>("male");
  const [age, setAge] = useState<number>(35);
  const [restingHR, setRestingHR] = useState<number>(60);
  const [calculated, setCalculated] = useState(false);

  const vo2max = useMemo(() => {
    const maxHR = 211 - 0.64 * age;
    return Math.round(15.3 * (maxHR / restingHR) * 10) / 10;
  }, [age, restingHR]);

  const vurdering = useMemo(() => classify(vo2max, gender, age), [vo2max, gender, age]);
  const tips = useMemo(() => TIPS_BY_LEVEL[vurdering.level] || TIPS_BY_LEVEL.mid, [vurdering.level]);
  const ageRow = REFERENCE_TABLE[gender][ageGroup(age)];

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen">
      <SiteHeader />

      <section className="px-6 pt-12 pb-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">Verktøy</p>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-4">VO2 Max-kalkulator</h1>
          <p className="text-stone-600 text-lg font-light max-w-xl mx-auto mb-8">
            Beregn på 30 sekunder. Få personlig referansetabell og tre tips tilpasset ditt nivå.
          </p>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-stone-200/60 aspect-[16/9] max-w-2xl mx-auto">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/images/tools/vo2-max-kalkulator.jpg" alt="VO2 Max-kalkulator" className="w-full h-full object-cover" loading="eager"/>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-3xl border border-stone-200 p-8 md:p-10">
            <div className="space-y-8">
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-3 font-medium">Kjønn</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setGender("male"); setCalculated(false); }}
                    className={`px-5 py-3 rounded-2xl text-sm font-medium border transition-all ${gender === "male" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-900"}`}
                  >
                    Mann
                  </button>
                  <button
                    onClick={() => { setGender("female"); setCalculated(false); }}
                    className={`px-5 py-3 rounded-2xl text-sm font-medium border transition-all ${gender === "female" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-900"}`}
                  >
                    Kvinne
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="text-xs uppercase tracking-wider text-stone-500 font-medium">Alder</label>
                  <span className="text-2xl font-light tracking-tight">{age} <span className="text-stone-500 text-sm">år</span></span>
                </div>
                <input
                  type="range"
                  min={18}
                  max={80}
                  value={age}
                  onChange={(e) => { setAge(parseInt(e.target.value)); setCalculated(false); }}
                  className="w-full accent-stone-900"
                />
                <div className="flex justify-between text-xs text-stone-400 mt-2"><span>18</span><span>80</span></div>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="text-xs uppercase tracking-wider text-stone-500 font-medium">Hvilepuls</label>
                  <span className="text-2xl font-light tracking-tight">{restingHR} <span className="text-stone-500 text-sm">bpm</span></span>
                </div>
                <input
                  type="range"
                  min={40}
                  max={100}
                  value={restingHR}
                  onChange={(e) => { setRestingHR(parseInt(e.target.value)); setCalculated(false); }}
                  className="w-full accent-stone-900"
                />
                <div className="flex justify-between text-xs text-stone-400 mt-2"><span>40 (godt trent)</span><span>100 (lavt fysisk nivå)</span></div>
                <p className="text-xs text-stone-500 mt-3 leading-relaxed">
                  💡 Mål hvilepuls om morgenen før du står opp. Ligg stille i 1 minutt og tell pulsslag i 60 sekunder.
                </p>
              </div>

              <button
                onClick={() => setCalculated(true)}
                className="w-full bg-stone-900 hover:bg-stone-800 text-white py-4 rounded-full font-medium transition-all"
              >
                Beregn mitt VO2 Max
              </button>
            </div>
          </div>

          {calculated && (
            <div className="mt-6 bg-stone-900 text-white rounded-3xl p-10 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">Ditt resultat</p>
              <div className="text-7xl md:text-8xl font-extralight tracking-tight mb-2">{vo2max}</div>
              <p className="text-sm text-stone-400 mb-6">ml O₂ / kg / min</p>
              <div className={`inline-block px-6 py-2 rounded-full bg-white ${vurdering.color} font-medium text-base`}>
                {vurdering.label}
              </div>
              <p className="text-stone-400 text-sm mt-6 max-w-md mx-auto">
                Basert på Uth-metoden. For din aldersgruppe ({ageRow.age}, {gender === "male" ? "menn" : "kvinner"}) er &gt;{ageRow.good} regnet som god.
              </p>
            </div>
          )}

          {calculated && (
            <div className="mt-6 bg-white rounded-3xl border border-stone-200 p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">3 tips for ditt nivå</p>
              <h3 className="text-2xl font-light tracking-tight mb-6">Slik forbedrer du VO2 Max</h3>
              <ol className="space-y-5">
                {tips.map((t, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="text-3xl font-light text-stone-300 leading-none w-8 shrink-0">{i + 1}</span>
                    <p className="text-stone-700 leading-relaxed">{t}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {calculated && (
            <div className="mt-6 bg-stone-100 rounded-3xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-200 to-sky-200 flex items-center justify-center text-stone-700 shrink-0">⚡</div>
                <div className="flex-1">
                  <h3 className="font-medium mb-1">Vil du forbedre VO2 Max?</h3>
                  <p className="text-stone-600 text-sm mb-4 leading-relaxed">
                    En treningscoach lager et 8-ukers program tilpasset deg, dine mål og hverdagen din.
                  </p>
                  <Link href="/coacher?category=trening" className="inline-block bg-stone-900 hover:bg-stone-800 text-white px-6 py-3 rounded-full text-sm font-medium transition-all">
                    Finn en treningscoach →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {calculated && <MagnetCapture magnet="trening" track="lop" source="verktoy:vo2max" />}
        </div>
      </section>

      <section className="px-6 py-20 border-t border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">Referansetabell</p>
          <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-2">Hva er bra VO2 Max?</h2>
          <p className="text-stone-600 mb-10">Sammenlign din verdi med ACSM-referansetall for {gender === "male" ? "menn" : "kvinner"}.</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left p-3 font-medium">Alder</th>
                  <th className="text-center p-3 font-medium text-red-600">Dårlig</th>
                  <th className="text-center p-3 font-medium text-amber-600">Under</th>
                  <th className="text-center p-3 font-medium text-stone-700">Middels</th>
                  <th className="text-center p-3 font-medium text-emerald-600">God</th>
                  <th className="text-center p-3 font-medium text-emerald-700">Utmerket</th>
                </tr>
              </thead>
              <tbody>
                {REFERENCE_TABLE[gender].map((row) => {
                  const isYourRow = ageRow.age === row.age;
                  return (
                    <tr key={row.age} className={`border-b border-stone-100 ${isYourRow ? "bg-stone-50" : ""}`}>
                      <td className="p-3 font-medium">{row.age}{isYourRow && <span className="ml-2 text-xs text-stone-400">(deg)</span>}</td>
                      <td className="text-center p-3 text-stone-600">&lt; {row.poor}</td>
                      <td className="text-center p-3 text-stone-600">{row.poor}–{row.below}</td>
                      <td className="text-center p-3 text-stone-600">{row.below}–{row.mid}</td>
                      <td className="text-center p-3 text-stone-600">{row.mid}–{row.good}</td>
                      <td className="text-center p-3 text-stone-600">&gt; {row.good}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-stone-400 mt-4">Verdier i ml O₂ / kg / min. Basert på ACSM (American College of Sports Medicine) og norske idrettslaboratorie-referanser.</p>
        </div>
      </section>

      <section className="px-6 py-20 border-t border-stone-200">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">Vanlige spørsmål</p>
          <h2 className="text-3xl font-light tracking-tight mb-10">Om VO2 Max</h2>

          <div className="space-y-3">
            {[
              {
                q: "Hva er VO2 Max?",
                a: "VO2 Max er det maksimale volumet oksygen kroppen din kan ta opp og bruke per minutt under intens fysisk aktivitet. Det måles i milliliter oksygen per kilogram kroppsvekt per minutt (ml/kg/min) og regnes som den beste enkeltmålingen av aerob utholdenhet.",
              },
              {
                q: "Hva er bra VO2 Max?",
                a: "For en mann i 30-årene er 39–45 middels, 45–52 god, og over 52 utmerket. For kvinner i 30-årene er 33–38 middels, 38–44 god, og over 44 utmerket. Verdien må alltid sammenlignes med din alder og kjønn.",
              },
              {
                q: "Hvor nøyaktig er denne kalkulatoren?",
                a: "Estimater basert på alder og hvilepuls (Uth-metoden) har en feilmargin på ±10–15 % sammenlignet med direkte måling i laboratorium. For mer presis måling, ta en Cooper-test (12-min løp) eller besøk et idrettslaboratorium.",
              },
              {
                q: "Hvordan kan jeg forbedre VO2 Max?",
                a: "De tre mest effektive metodene er: (1) intervalltrening — 4×4 min på 90 % maks puls med 3 min pause, (2) lange utholdenhetsøkter på 60–90 min på moderat intensitet, og (3) konsistens — minst 3 økter i uken i 8 uker. NTNU-forskning viser at 10–15 % forbedring er realistisk.",
              },
              {
                q: "Hvorfor synker VO2 Max med alderen?",
                a: "Aldersrelatert nedgang er ca. 1 % per år fra 30-årene, men trent du regelmessig kan du holde 75 % av VO2 Max sammenlignet med en 25-åring også som 60-åring. Genetikk forklarer ca. 50 % av forskjellene mellom mennesker.",
              },
            ].map((item) => (
              <details key={item.q} className="group bg-white rounded-2xl border border-stone-200 p-6">
                <summary className="font-medium flex items-center justify-between list-none cursor-pointer">
                  <span>{item.q}</span>
                  <span className="text-stone-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-stone-600 mt-4 text-sm leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-stone-900 text-white text-center">
        <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-4">Klar for å forbedre kondisjonen?</h2>
        <p className="text-stone-400 mb-10 max-w-md mx-auto">En treningscoach lager program tilpasset deg. Første 15-min samtale er gratis.</p>
        <Link href="/coacher?category=trening" className="inline-block bg-white hover:bg-stone-100 text-stone-900 px-10 py-5 rounded-full font-medium transition-all">
          Finn en treningscoach
        </Link>
      </section>

      <footer className="py-12 px-6 text-center text-xs text-stone-400">
        <p>© 2026 Coachguiden · Beregning basert på Uth-metoden (Uth N et al., 2004) og ACSM-referansetabeller.</p>
      </footer>
    </div>
  );
}
